/**
 * La langue de l'app mobile — sans i18next, et c'est voulu.
 *
 * `src/i18n.ts` est interdit au mobile (`mobile:check`, #99) : il précharge ses 24
 * espaces de noms, 608 Ko au démarrage. Ici, le français est embarqué (les
 * dictionnaires `fr/*.json`, un par zone d'écran) et UNE seule autre langue est
 * chargée à la demande (`locales/{lng}.json`, un fichier par langue, que Vite
 * découpe en morceaux séparés).
 *
 * **Changer de langue remonte l'app** (`useLangueMobile` sert de clé à la coque).
 * `t()` est donc une simple fonction, appelable dans un helper comme dans un
 * composant : rien n'a besoin de s'abonner, puisque tout est rendu à neuf.
 *
 * Les clés sont `zone.cle` : la zone est le nom du fichier `fr/{zone}.json`.
 * Repli : langue courante → français → la clé elle-même (visible, donc repérable).
 */

import { LANGUES_MOBILES, type LangueMobile } from '../../constants/appLocales';

export type Dictionnaire = { [cle: string]: string | Dictionnaire };

export { LANGUES_MOBILES, NOMS_LANGUES } from '../../constants/appLocales';
export type { LangueMobile } from '../../constants/appLocales';

const LANGUES_RTL: readonly string[] = ['ar'];

/** Retenue d'un lancement à l'autre : l'app rouvre dans sa langue avant `/user`. */
const CLE_STOCKAGE = 'TF_MOBILE_LOCALE';

function chargerFrancais(): Dictionnaire {
    const modules = import.meta.glob<{ default: Dictionnaire }>('./fr/*.json', { eager: true });
    const dico: Dictionnaire = {};
    for (const [chemin, module] of Object.entries(modules)) {
        const zone = chemin.replace('./fr/', '').replace('.json', '');
        dico[zone] = module.default;
    }

    return dico;
}

const FRANCAIS = chargerFrancais();

const AUTRES = import.meta.glob<{ default: Dictionnaire }>('./locales/*.json');

let langueCourante: LangueMobile = 'fr';
let dictionnaire: Dictionnaire = FRANCAIS;
const abonnes = new Set<() => void>();

export function estLangueMobile(valeur: string | null | undefined): valeur is LangueMobile {
    return !!valeur && (LANGUES_MOBILES as readonly string[]).includes(valeur);
}

/** `fr-FR` → `fr`, `pt_BR` → `pt` ; une langue inconnue retombe sur le français. */
export function normaliserLangue(valeur: string | null | undefined): LangueMobile {
    const courte = (valeur ?? '').toLowerCase().split(/[-_]/)[0];

    return estLangueMobile(courte) ? courte : 'fr';
}

export function langueMobile(): LangueMobile {
    return langueCourante;
}

export function directionLangue(langue: string = langueCourante): 'rtl' | 'ltr' {
    return LANGUES_RTL.includes(langue) ? 'rtl' : 'ltr';
}

function lire(dico: Dictionnaire, cle: string): string | undefined {
    let noeud: string | Dictionnaire | undefined = dico;
    for (const morceau of cle.split('.')) {
        if (typeof noeud !== 'object' || noeud === null) return undefined;
        noeud = noeud[morceau];
    }

    return typeof noeud === 'string' ? noeud : undefined;
}

function interpoler(texte: string, variables?: Record<string, string | number>): string {
    if (!variables) return texte;

    return texte.replace(/\{(\w+)\}/g, (brut, nom: string) =>
        nom in variables ? String(variables[nom]) : brut,
    );
}

/**
 * Traduire une clé. `n` choisit la forme plurielle : `cle_one`, `cle_few`… selon
 * `Intl.PluralRules` de la langue, puis `cle_other`, puis `cle` sans suffixe.
 */
export function t(cle: string, variables?: Record<string, string | number>): string {
    let effective = cle;
    if (variables && typeof variables.n === 'number') {
        // Forme CLDR exacte (`_few` du russe, `_two` de l'arabe…), puis `_other`.
        const forme = new Intl.PluralRules(langueCourante).select(variables.n);
        const candidate = [`${cle}_${forme}`, `${cle}_other`].find(
            (c) => lire(dictionnaire, c) !== undefined || lire(FRANCAIS, c) !== undefined,
        );
        if (candidate) effective = candidate;
    }

    const texte = lire(dictionnaire, effective) ?? lire(FRANCAIS, effective) ?? effective;

    return interpoler(texte, variables);
}

function appliquerAuDocument(langue: LangueMobile): void {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = langue;
    document.documentElement.dir = directionLangue(langue);
}

function notifier(): void {
    abonnes.forEach((abonne) => abonne());
}

/**
 * Passer l'app dans une langue. Le dictionnaire est chargé AVANT de basculer :
 * l'écran ne montre jamais un mélange de clés et de textes pendant le chargement.
 * Un chargement en échec (réseau coupé) laisse la langue en place.
 */
export async function chargerLangue(demande: string | null | undefined): Promise<void> {
    const langue = normaliserLangue(demande);
    if (langue === langueCourante) return;

    let suivant: Dictionnaire = FRANCAIS;
    if (langue !== 'fr') {
        const chargeur = AUTRES[`./locales/${langue}.json`];
        if (!chargeur) return;
        try {
            suivant = (await chargeur()).default;
        } catch {
            return;
        }
    }

    langueCourante = langue;
    dictionnaire = suivant;
    appliquerAuDocument(langue);
    try {
        localStorage.setItem(CLE_STOCKAGE, langue);
    } catch {
        /* Stockage indisponible : l'app rouvrira en français, puis suivra /user. */
    }
    notifier();
}

/**
 * La langue de départ, avant toute réponse de `/user` : celle du dernier
 * lancement, sinon celle du téléphone (l'écran de connexion et le guide
 * d'installation précèdent le compte).
 */
export function langueDeDepart(): LangueMobile {
    try {
        const retenue = localStorage.getItem(CLE_STOCKAGE);
        if (estLangueMobile(retenue)) return retenue;
    } catch {
        /* rien */
    }

    return normaliserLangue(typeof navigator !== 'undefined' ? navigator.language : 'fr');
}

export function abonnerLangue(abonne: () => void): () => void {
    abonnes.add(abonne);

    return () => abonnes.delete(abonne);
}

/** Formats de date et de nombre dans la langue de l'app. */
export function formatDate(date: Date | string, options: Intl.DateTimeFormatOptions): string {
    return new Intl.DateTimeFormat(langueCourante, options).format(typeof date === 'string' ? new Date(date) : date);
}

/** Réservé aux tests : rend le module à son état neuf. */
export function reinitialiserLangue(): void {
    langueCourante = 'fr';
    dictionnaire = FRANCAIS;
    abonnes.clear();
}
